import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { type ApiFormat, FORMAT_NAMES, MODEL_FAMILIES } from '../data/models';

const formats = (list: readonly ApiFormat[]) => list.map((f) => FORMAT_NAMES[f]).join(', ');
const delay = (seconds: number) => ({ '--d': `${String(seconds)}s` }) as CSSProperties;

/** Wide layout: the trunk enters the interchange, four lines fan out at 45°. */
function WideMap({ app }: { app: string }) {
  const rows = [42, 112, 188, 258];
  return (
    <svg className="route-map-wide" viewBox="0 0 1200 300" aria-hidden="true" focusable="false">
      <path
        className="rm-line"
        pathLength={1}
        d="M48 150H480"
        stroke="var(--ink)"
        strokeWidth="10"
      />
      {MODEL_FAMILIES.map((family, i) => {
        const y = rows[i] ?? 150;
        const bend = Math.abs(150 - y);
        return (
          <path
            key={family.id}
            className="rm-line"
            style={delay(0.45 + i * 0.08)}
            pathLength={1}
            d={`M676 150H700L${String(700 + bend)} ${String(y)}H1030`}
            stroke={family.color}
            strokeWidth="8"
          />
        );
      })}
      <circle className="rm-stop" cx="48" cy="150" r="10" stroke="var(--ink)" />
      <text className="rm-name" x="30" y="118">
        {app}
      </text>
      <rect x="480" y="116" width="196" height="68" rx="34" className="rm-hub" />
      <text className="rm-name rm-hub-name" x="578" y="159" textAnchor="middle">
        LowCostRouter
      </text>
      <text className="rm-address" x="578" y="216" textAnchor="middle">
        api.lowcostrouter.com
      </text>
      {MODEL_FAMILIES.map((family, i) => {
        const y = rows[i] ?? 150;
        return (
          <g key={family.id} className="rm-stop-group" style={delay(0.9 + i * 0.08)}>
            <circle className="rm-stop" cx="1030" cy={y} r="10" stroke={family.color} />
            <text className="rm-name" x="1056" y={y + 8}>
              {family.name}
            </text>
            <text className="rm-meta" x="1056" y={y + 30}>
              {formats(family.formats)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Narrow layout: four parallel lines leave the interchange and peel off one by one. */
function NarrowMap({ app }: { app: string }) {
  return (
    <svg className="route-map-narrow" viewBox="0 0 340 540" aria-hidden="true" focusable="false">
      <path className="rm-line" pathLength={1} d="M44 34V150" stroke="var(--ink)" strokeWidth="8" />
      {MODEL_FAMILIES.map((family, i) => {
        const x = 62 - i * 12;
        const y = 300 + i * 58;
        return (
          <path
            key={family.id}
            className="rm-line"
            style={delay(0.45 + i * 0.08)}
            pathLength={1}
            d={`M${String(x)} 246V${String(y - 20)}L${String(x + 20)} ${String(y)}H150`}
            stroke={family.color}
            strokeWidth="7"
          />
        );
      })}
      <circle className="rm-stop" cx="44" cy="34" r="9" stroke="var(--ink)" />
      <text className="rm-name rm-small" x="70" y="41">
        {app}
      </text>
      <rect x="14" y="150" width="60" height="96" rx="30" className="rm-hub" />
      <text className="rm-name rm-small" x="92" y="192">
        LowCostRouter
      </text>
      <text className="rm-address rm-small" x="92" y="214">
        api.lowcostrouter.com
      </text>
      {MODEL_FAMILIES.map((family, i) => {
        const y = 300 + i * 58;
        return (
          <g key={family.id} className="rm-stop-group" style={delay(0.9 + i * 0.08)}>
            <circle className="rm-stop" cx="150" cy={y} r="9" stroke={family.color} />
            <text className="rm-name rm-small" x="172" y={y + 7}>
              {family.name}
            </text>
            <text className="rm-meta rm-small" x="172" y={y + 27}>
              {formats(family.formats)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Hero illustration: how a request travels from the user's app to a model. */
export function RouteMap() {
  const { t } = useTranslation();
  const app = t('landing.map.app');
  return (
    <figure className="route-map" role="img" aria-label={t('landing.map.label')}>
      <WideMap app={app} />
      <NarrowMap app={app} />
    </figure>
  );
}
